import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetSessionsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetSessionsState
  extends MergeRequestState<GetSessionsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getSessions,
  isFetching: (state: ReduxState) => state.server.getSessions.isFetching,
  data: (state: ReduxState) => state.server.getSessions.data,
  error: (state: ReduxState) => state.server.getSessions.error,
};

const initialState: GetSessionsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getSessions';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  ({ config }: { config?: ServerApiConfig } | void = {}, { rejectWithValue }) =>
    serverApi.getSessions(config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetSessions {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getSessions: ServerGetSessions = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
